import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { FileService } from 'src/app/shared/services/file-service';
import { DocumentService } from 'src/app/data/document.service';
import { LegalizationDocumentStatusEnum } from '../../models/enums/legalization-document-status-enum';
import { TranslateService } from '@ngx-translate/core';
import { ViewLegalizationDto, ViewLegalizationDocumentDto, LegalizationDocumentDto } from '../../models/dtos/legalization-document-files-dto';
import { LegalizationFilesModalComponent, LegalizationFilesModalData } from '../legalization-files-modal/legalization-files-modal.component';
import { MatDialogConfig } from '@angular/material/dialog';
import { LegalizationFileDto } from '../../models/legalization-file-dto';
import { SaveLegalizationInfoByEmployeeComponent } from '../save-info-modals/save-legalization-info-by-employee/save-legalization-info-by-employee.component';
import { SaveLegalizationInfoByCoordinatorComponent } from '../save-info-modals/save-legalization-info-by-coordinator/save-legalization-info-by-coordinator.component';
import { SaveLegalizationInfoByLegalizerModalComponent } from '../save-info-modals/save-legalization-info-by-legalizer/save-legalization-info-by-legalizer-modal.component';
import { AddLegalizationInformationModalData } from '../save-info-modals/AddLegalizationInformationModalData';
import { Subject } from 'rxjs';
import { AssignLegalizationDocumentComponent } from '../assign-legalization-document/assign-legalization-document.component';
import { AssignLegalizationDocumentData } from '../save-info-modals/AssignLegalizationDocumentData';
import { EditLegalizationDocumentModalComponent, EditLegalizationDocumentModalData } from '../save-info-modals/components/edit-legalization-document/edit-legalization-document-modal.component';
import { AuthService } from 'src/app/core/authentication/auth.service';
import { Permission } from 'src/app/common/enums/permissions';
import { LegalizationStatusHelperService } from '../services/legalization-status-helper-service';

@Component({
  selector: 'app-expansive-leagalization-documents-list',
  templateUrl: './expansive-leagalization-documents-list.component.html',
  styleUrls: ['./expansive-leagalization-documents-list.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed, void', style({ height: '0px', minHeight: '0', display: 'none' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  standalone: false
})
export class ExpansiveLeagalizationDocumentsListComponent implements OnInit {
  protected dialog = inject(MatDialog);
  protected fileService = inject(FileService);
  protected documentService = inject(DocumentService);
  protected translateService = inject(TranslateService);
  protected spinner = inject(NgxSpinnerService);
  protected authService = inject(AuthService);
  protected legalizationStatusHelper = inject(LegalizationStatusHelperService);

  @Input() workerId: number;
  @Input() documentsExpansionHeaderHeight: string;
  @Input() showDecisionButton: boolean;

  @Output() newLegalizationDocumentEvent = new EventEmitter<LegalizationDocumentDto>();

  legalizations: ViewLegalizationDto[];
  expandedDocuments: Set<number> = new Set<number>();
  documentsDisplayedColumns: string[] = ['expand', 'documentType', 'description', 'startDate', 'endDate', 'status', 'actions'];
  previousVersionsDisplayedColumns: string[] = ['expand', 'documentType', 'description', 'startDate', 'endDate', 'status', 'actions'];
  protected hasDocumentsRequiringCorrection = false;
  protected subject = new Subject<void>();
  showActiveOnly: boolean = false;

  public LegalizationDocumentStatusEnum = LegalizationDocumentStatusEnum;


  constructor() {
  }

  public ngOnInit(): void {
    this.fetchLegalizationDocuments();
  }

  private dialogConfig<T>(data: T): MatDialogConfig<T> {
    return {
      data,
      maxWidth: '90vw',
      maxHeight: '100vh'
    };
  }

  private fetchLegalizationDocuments() {
    this.spinner.show();
    this.documentService.getLegalizationDocumentsByWorkerId(this.workerId, this.showActiveOnly)
      .subscribe({
        next: (response: ViewLegalizationDto[]) => {
          this.legalizations = response;
          this.spinner.hide();
        },
        error: () => {
          this.spinner.hide();
        }
      });
  }

  toggleDocumentExpansion(documentId: number): void {
    if (this.expandedDocuments.has(documentId)) {
      this.expandedDocuments.delete(documentId);
    } else {
      this.expandedDocuments.add(documentId);
    }
  }

  isDocumentExpanded(documentId: number): boolean {
    return this.expandedDocuments.has(documentId);
  }

  hasPreviousVersions = (index: number, document: ViewLegalizationDocumentDto): boolean => {
    return document.HasPreviousVersions;
  }

  openLegalizationFilesModal(legalizationFiles: LegalizationFileDto[], workerId: number): void {
    const dialogRef = this.dialog.open(
      LegalizationFilesModalComponent,
      this.dialogConfig(<LegalizationFilesModalData>{
        LegalizationFiles: legalizationFiles,
        WorkerId: workerId
      }),
    );
  }

  openDocumentLegalizationForEmployee(legalizationDocumentId: number, isCorrection: boolean = false, legalizationId: number | undefined = undefined): void {
    const dialogRef = this.dialog.open(
      SaveLegalizationInfoByEmployeeComponent,
      this.dialogConfig(<AddLegalizationInformationModalData>{
        Title: 'Legalization.Title',
        LegalizationDocumentId: legalizationDocumentId,
        WorkerId: this.workerId,
        IsCorrection: isCorrection,
        LegalizationId: legalizationId,
      }),
    );

    dialogRef.afterClosed().subscribe((result) => this.handleDialogResponse(result));
  }

  openDocumentLegalizationForCoordinator(legalizationDocumentId: number, isCorrection: boolean = false): void {
    const dialogRef = this.dialog.open(
      SaveLegalizationInfoByCoordinatorComponent,
      this.dialogConfig(<AddLegalizationInformationModalData>{
        Title: 'Legalization.Title',
        LegalizationDocumentId: legalizationDocumentId,
        WorkerId: this.workerId,
        IsCorrection: isCorrection,
      }),
    );

    dialogRef.afterClosed().subscribe((result) => this.handleDialogResponse(result));
  }

  openDocumentLegalizationForLegalizer(legalizationDocumentId: number, isCorrection: boolean = false, legalizationId: number | undefined = undefined): void {
    const dialogRef = this.dialog.open(
      SaveLegalizationInfoByLegalizerModalComponent,
      this.dialogConfig(<AddLegalizationInformationModalData>{
        Title: 'Legalization.Title',
        LegalizationDocumentId: legalizationDocumentId,
        WorkerId: this.workerId,
        IsCorrection: isCorrection,
        PreSelectedLegalizationsIds: legalizationId ? [legalizationId] : []
      }),
    );

    dialogRef.afterClosed().subscribe((result) => this.handleDialogResponse(result));
  }

  openAssigningDocuments(workerId: number, legalizationDocumentId: number): void {
    const dialogRef = this.dialog.open(
      AssignLegalizationDocumentComponent,
      this.dialogConfig(<AssignLegalizationDocumentData>{
        Title: this.translateService.instant('Legalization.AssignDocuments'),
        WorkerId: workerId,
        LegalizationDocumentId: legalizationDocumentId,
      }),
    );

    dialogRef.afterClosed().subscribe((result) => this.handleDialogResponse(result));
  }

  openEditDocument(legalizationDocumentId: number): void {
    const dialogRef = this.dialog.open(
      EditLegalizationDocumentModalComponent,
      this.dialogConfig(<EditLegalizationDocumentModalData>{
        LegalizationDocumentId: legalizationDocumentId,
        WorkerId: this.workerId
      }),
    );

    dialogRef.afterClosed().subscribe((result) => this.handleDialogResponse(result));
  }

  handleCorrection(documentId: number): void {
    if (this.authService.hasPermission(Permission.LegalizationDecision)) {
      this.openDocumentLegalizationForLegalizer(documentId, true);
    } else if (this.authService.hasPermission(Permission.CorrectLegalizationDocumentByCoordinator)) {
      this.openDocumentLegalizationForCoordinator(documentId, true);
    } else if (this.authService.hasPermission(Permission.CorrectLegalizationDocumentByEmployee)) {
      this.openDocumentLegalizationForEmployee(documentId, true);
    }
  }

  handleAddDocument(documentId: number, legalizationId: number | undefined = undefined): void {
    if (this.authService.hasPermission(Permission.LegalizationDecision)) {
      this.openDocumentLegalizationForLegalizer(documentId, false, legalizationId);
    } else if (this.authService.hasPermission(Permission.AddLegalizationDocument)) {
      this.openDocumentLegalizationForCoordinator(documentId);
    } else if (this.authService.hasPermission(Permission.AddBasicLegalizationDocument)) {
      this.openDocumentLegalizationForEmployee(documentId, false, legalizationId);
    }
  }

  onShowActiveOnlyChange(): void {
    this.fetchLegalizationDocuments();
  }

  private handleDialogResponse(result: any): void {
    if (!result) {
      return;
    }

    if (result.isSuccess) {
      this.subject.next();
      this.fetchLegalizationDocuments();
    }
  }
}
